import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Pencil, Plus, Search, Send, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { api, del, patch, post, put } from "../../lib/api";
import { useCan } from "../../lib/session";
import { Avatar, Card, Empty, ErrorNote, Field, fmtDate, Modal, PageHeader } from "../../components/ui";
import { useOrg, type Org } from "./organisation";

interface PersonRow {
  id: string;
  displayName: string;
  email: string | null;
  status: string;
  positionTitle: string | null;
  lastSeenAt: string | null;
  grantCount: number;
}
interface Grant {
  id: string;
  personId: string;
  personName: string;
  personEmail: string | null;
  roleKey: string;
  roleName: string;
  scopeType: string;
  scopeId: string | null;
  scopeName: string | null;
  validTo: string | null;
  reason: string | null;
  createdAt: string;
  grantedByName: string | null;
}
interface Role {
  id: string;
  key: string;
  name: string;
  description: string;
  permissions: string[];
  assignableScopeTypes: string[];
  isSystem: boolean;
  version: number;
  activeGrants: number;
}
interface Permission {
  key: string;
  description: string;
  risk: string;
}

const useRoles = () => useQuery({ queryKey: ["roles"], queryFn: () => api<{ items: Role[] }>("/api/authz/roles") });

function scopeOptions(org: Org, scopeType: string) {
  switch (scopeType) {
    case "company": return org.companies.map((x) => ({ id: x.id, name: x.name }));
    case "business_unit": return org.businessUnits.map((x) => ({ id: x.id, name: x.name }));
    case "department": return org.departments.map((x) => ({ id: x.id, name: `${x.name} (${org.companies.find((c) => c.id === x.companyId)?.code ?? ""})` }));
    case "site": return org.sites.map((x) => ({ id: x.id, name: x.name }));
    case "team": return org.teams.map((x) => ({ id: x.id, name: x.name }));
    default: return [];
  }
}

/** Grant a role, or (with `grant`) edit one: role, scope or expiry. */
function GrantDialog({ personId, grant, onClose }: { personId: string; grant?: Grant; onClose: () => void }) {
  const qc = useQueryClient();
  const roles = useRoles();
  const org = useOrg();
  const [roleKey, setRoleKey] = useState(grant?.roleKey ?? "");
  const [scopeType, setScopeType] = useState(grant?.scopeType ?? "");
  const [scopeId, setScopeId] = useState(grant?.scopeId ?? "");
  const [reason, setReason] = useState("");
  const [validTo, setValidTo] = useState(grant?.validTo ? grant.validTo.slice(0, 10) : "");
  const role = roles.data?.items.find((r) => r.key === roleKey);
  const m = useMutation({
    mutationFn: () => {
      const body = { roleKey, scopeType, scopeId: scopeType === "group" ? null : scopeId, reason: reason || undefined, validTo: validTo ? new Date(validTo).toISOString() : null };
      return grant ? api(`/api/authz/grants/${grant.id}`, { method: "PUT", json: body }) : post("/api/authz/grants", { ...body, personId, validTo: body.validTo ?? undefined });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["grants"] });
      void qc.invalidateQueries({ queryKey: ["people"] });
      onClose();
    },
  });
  const opts = org.data && scopeType && scopeType !== "group" ? scopeOptions(org.data, scopeType) : [];
  return (
    <Modal
      title={grant ? `Edit ${grant.roleName}` : "Grant a role"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!roleKey || !scopeType || (scopeType !== "group" && !scopeId) || m.isPending} onClick={() => m.mutate()}>
            {grant ? "Save" : "Grant"}
          </button>
        </>
      }
    >
      <ErrorNote error={m.error} />
      <Field label="Role">
        <select className="input" value={roleKey} onChange={(e) => { setRoleKey(e.target.value); setScopeType(""); setScopeId(""); }}>
          <option value="">Choose a role…</option>
          {roles.data?.items.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
      </Field>
      {role && <p className="muted text-xs">{role.description}</p>}
      {role && (
        <Field label="Scope" hint="Where this role applies. Group = everything.">
          <select className="input" value={scopeType} onChange={(e) => { setScopeType(e.target.value); setScopeId(""); }}>
            <option value="">Choose…</option>
            {role.assignableScopeTypes.filter((s) => s !== "project").map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </Field>
      )}
      {opts.length > 0 && (
        <Field label="Which one">
          <select className="input" value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
            <option value="">Choose…</option>
            {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Expires (optional)"><input className="input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} /></Field>
      <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. New Mythos duty manager" /></Field>
    </Modal>
  );
}

function GrantList({ grants, canManage }: { grants: Grant[]; canManage: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Grant | null>(null);
  const revoke = useMutation({
    mutationFn: (g: Grant) => del(`/api/authz/grants/${g.id}`, { reason: "Revoked in admin" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["grants"] });
      void qc.invalidateQueries({ queryKey: ["people"] });
    },
  });
  if (grants.length === 0) return <Empty>No roles granted.</Empty>;
  return (
    <>
      <ErrorNote error={revoke.error} />
      <div className="overflow-x-auto">
        <table className="table">
          <thead><tr><th>Role</th><th>Scope</th><th>Granted</th><th>Expires</th><th /></tr></thead>
          <tbody>
            {grants.map((g) => (
              <tr key={g.id}>
                <td className="font-medium">{g.roleName}</td>
                <td>{g.scopeType === "group" ? "Whole group" : `${g.scopeType.replace("_", " ")}: ${g.scopeName ?? g.scopeId}`}</td>
                <td className="muted">{fmtDate(g.createdAt)}{g.grantedByName ? ` · ${g.grantedByName}` : ""}</td>
                <td className="muted">{g.validTo ? fmtDate(g.validTo) : "—"}</td>
                <td className="text-right">
                  {canManage && (
                    <button className="btn mr-1.5 !min-h-0 !py-1" onClick={() => setEditing(g)} aria-label={`Edit ${g.roleName}`} title="Change role, scope or expiry"><Pencil size={14} /></button>
                  )}
                  {canManage && (
                    <button className="btn btn-danger !min-h-0 !py-1" disabled={revoke.isPending} onClick={() => confirm(`Revoke ${g.roleName}?`) && revoke.mutate(g)} aria-label="Revoke">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <GrantDialog personId={editing.personId} grant={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** The two access templates: Staff, or Management for the companies picked. */
type Access = { template: "staff" | "manager" | "management"; allCompanies: boolean; companyIds: string[] };
const TEMPLATE_NAME = { staff: "Staff", manager: "Manager", management: "Group Management" } as const;
const TEMPLATE_HELP = {
  staff: "Their own tasks, files and notes, what is assigned or shared with them, and the workspaces (teams) they are added to.",
  manager: "Like Staff, plus: creates their own workspaces, invites staff into them and runs the team (dashboard, assigning). No company-wide data.",
  management: "Everything in the companies picked: tasks, documents, projects, approvals, HR and finance. Never your private items or Wealth.",
};
function AccessPicker({ value, onChange }: { value: Access; onChange: (a: Access) => void }) {
  const org = useOrg();
  const companies = (org.data?.companies ?? []).filter((c) => c.active);
  return (
    <div className="space-y-2">
      {(["staff", "manager", "management"] as const).map((t) => (
        <label key={t} className={`flex cursor-pointer gap-2.5 rounded-lg border p-2.5 text-sm ${value.template === t ? "border-brand-500 bg-brand-50/50" : "border-[var(--line)]"}`}>
          <input type="radio" className="mt-0.5" checked={value.template === t} onChange={() => onChange({ ...value, template: t, allCompanies: t === "management" ? (value.template === "management" ? value.allCompanies : true) : false })} />
          <span><span className="font-semibold">{TEMPLATE_NAME[t]}</span><span className="muted block text-xs">{TEMPLATE_HELP[t]}</span></span>
        </label>
      ))}
      {value.template === "management" && (
        <div className="rounded-lg border border-[var(--line)] p-2.5">
          <p className="section-label mb-1.5">Companies they manage</p>
          <label className="flex items-center gap-2 py-0.5 text-sm font-medium"><input type="checkbox" checked={value.allCompanies} onChange={(e) => onChange({ ...value, allCompanies: e.target.checked })} /> All companies</label>
          {!value.allCompanies && companies.map((c) => (
            <label key={c.id} className="flex items-center gap-2 py-0.5 text-sm">
              <input type="checkbox" checked={value.companyIds.includes(c.id)} onChange={(e) => onChange({ ...value, companyIds: e.target.checked ? [...value.companyIds, c.id] : value.companyIds.filter((x) => x !== c.id) })} />
              {c.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
const accessReady = (a: Access) => a.template === "staff" || a.allCompanies || a.companyIds.length > 0;

/** What a person has now, read from their grants. */
function currentAccess(grants: Grant[]): Access & { label: string; custom: string[] } {
  const mg = grants.filter((g) => g.roleKey === "management");
  const custom = grants.filter((g) => !["staff", "manager", "management"].includes(g.roleKey)).map((g) => g.roleName);
  if (mg.length) {
    const all = mg.some((g) => g.scopeType === "group");
    return { template: "management", allCompanies: all, companyIds: mg.filter((g) => g.scopeType === "company").map((g) => g.scopeId!), custom,
      label: `Group Management · ${all ? "all companies" : mg.map((g) => g.scopeName ?? "?").join(", ")}` };
  }
  if (grants.some((g) => g.roleKey === "manager")) return { template: "manager", allCompanies: false, companyIds: [], custom, label: "Manager" };
  if (grants.some((g) => g.roleKey === "staff")) return { template: "staff", allCompanies: false, companyIds: [], custom, label: "Staff" };
  return { template: "staff", allCompanies: false, companyIds: [], custom, label: custom.length ? "Custom roles only" : "No access yet" };
}

function AccessDialog({ personId, start, onClose }: { personId: string; start: Access; onClose: () => void }) {
  const qc = useQueryClient();
  const [a, setA] = useState<Access>(start);
  const save = useMutation({
    mutationFn: () => put(`/api/directory/people/${personId}/access`, a),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["grants", personId] }); void qc.invalidateQueries({ queryKey: ["people"] }); onClose(); },
  });
  return (
    <Modal title="Access" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!accessReady(a) || save.isPending} onClick={() => save.mutate()}>Save</button></>}>
      <ErrorNote error={save.error} />
      <AccessPicker value={a} onChange={setA} />
    </Modal>
  );
}

/**
 * Invite someone: add them (or find them by email), give them a role (Staff by default) and teams, and email them
 * the link to sign in with their work Microsoft account.
 */
function InviteDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", email: "", roleKey: "", teamIds: [] as string[], note: "", sendEmail: true });
  const [access, setAccess] = useState<Access>({ template: "staff", allCompanies: false, companyIds: [] });
  const [advanced, setAdvanced] = useState(false);
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api<{ items: Array<{ key: string; name: string; description: string | null }> }>("/api/authz/roles") });
  const teams = useQuery({ queryKey: ["dir-teams"], queryFn: () => api<{ items: Array<{ id: string; name: string }> }>("/api/directory/teams") });
  const invite = useMutation({
    mutationFn: () => post<{ created: boolean; teams: string[]; emailed: boolean; emailError: string | null }>("/api/directory/invite", { ...f, ...access, roleKey: advanced && f.roleKey ? f.roleKey : undefined, note: f.note || undefined }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["people"] }),
  });
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim());
  const outside = validEmail && !/@(infinite-fusion\.com|mythos\.com\.mt)$/i.test(f.email.trim());
  if (invite.data) {
    const r = invite.data;
    return (
      <Modal title="Invited" onClose={onClose} footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}>
        <p className="text-sm">{f.name} {r.created ? "has been added" : "was already in ONE and has been updated"}, with {advanced && f.roleKey ? `the ${roles.data?.items.find((x) => x.key === f.roleKey)?.name ?? f.roleKey} role` : `${TEMPLATE_NAME[access.template]} access`}{r.teams.length ? ` and in ${r.teams.join(", ")}` : ""}.</p>
        {f.sendEmail && (r.emailed
          ? <p className="mt-2 text-sm text-emerald-700">The invitation email went to {f.email.trim()}.</p>
          : <p className="mt-2 text-sm text-amber-700">The email could not be sent ({r.emailError}). Send them https://one.infinite-fusion.com yourself and tell them to sign in with Microsoft using {f.email.trim()}.</p>)}
        {!f.sendEmail && <p className="muted mt-2 text-sm">No email sent. Send them https://one.infinite-fusion.com and tell them to sign in with Microsoft using {f.email.trim()}.</p>}
      </Modal>
    );
  }
  return (
    <Modal title="Invite someone" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={f.name.trim().length < 2 || !validEmail || invite.isPending || (advanced ? !f.roleKey : !accessReady(access))} onClick={() => invite.mutate()}><Send size={15} /> {invite.isPending ? "Inviting…" : f.sendEmail ? "Invite and email" : "Invite"}</button></>}>
      <ErrorNote error={invite.error} />
      <div className="space-y-3">
        <Field label="Full name"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <Field label="Work email" hint="The Microsoft account they will sign in with.">
          <input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@infinite-fusion.com" />
        </Field>
        {outside && <p className="text-xs text-amber-700">This isn't an Infinite Fusion or Mythos address. They can only sign in if this address has an account on our Microsoft 365.</p>}
        <Field label="Access">
          {!advanced ? <AccessPicker value={access} onChange={setAccess} /> : (
            <select className="input" value={f.roleKey} onChange={(e) => setF({ ...f, roleKey: e.target.value })}>
              <option value="">Pick a role…</option>
              {roles.data?.items.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
          )}
          <button type="button" className="muted mt-1 text-xs hover:underline" onClick={() => setAdvanced((x) => !x)}>{advanced ? "Use a template (Staff / Manager / Group Management)" : "Advanced: give a specific role instead"}</button>
        </Field>
        <Field label="Teams (optional)">
          <div className="max-h-40 space-y-0.5 overflow-auto rounded-lg border border-[var(--line)] p-1.5">
            {teams.data?.items.map((t) => (
              <label key={t.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-black/[0.03]">
                <input type="checkbox" checked={f.teamIds.includes(t.id)} onChange={(e) => setF({ ...f, teamIds: e.target.checked ? [...f.teamIds, t.id] : f.teamIds.filter((x) => x !== t.id) })} />
                {t.name}
              </label>
            ))}
            {!teams.data?.items.length && <p className="muted px-1.5 py-1 text-sm">No teams.</p>}
          </div>
        </Field>
        <Field label="Personal note (optional)"><textarea className="input" rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Added to the email" /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.sendEmail} onChange={(e) => setF({ ...f, sendEmail: e.target.checked })} /> Email them the invitation now</label>
      </div>
    </Modal>
  );
}

/** Re-send (or send for the first time) the "how to get into ONE" email to someone already in ONE. */
function SendInviteDialog({ personId, name, email, onClose }: { personId: string; name: string; email: string; onClose: () => void }) {
  const [note, setNote] = useState("");
  const send = useMutation({ mutationFn: () => post(`/api/directory/people/${personId}/send-invite`, { note: note || undefined }) });
  if (send.isSuccess) return <Modal title="Invite email sent" onClose={onClose} footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}><p className="text-sm text-emerald-700">The email with how to sign in to ONE went to {email}.</p></Modal>;
  return (
    <Modal title={`Send ${name} the invite email`} onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={send.isPending} onClick={() => send.mutate()}><Send size={15} /> {send.isPending ? "Sending…" : "Send"}</button></>}>
      <ErrorNote error={send.error} />
      <p className="muted mb-3 text-sm">To {email}: the link to ONE, to sign in with Microsoft using this address, and the teams they're in. Their role and teams don't change.</p>
      <Field label="Personal note (optional)"><textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Added to the email" /></Field>
    </Modal>
  );
}

export function PeoplePage() {
  const [q, setQ] = useState("");
  const can = useCan();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteFor, setInviteFor] = useState<PersonRow | null>(null);
  const [form, setForm] = useState({ displayName: "", email: "" });
  const people = useQuery({ queryKey: ["people", q], queryFn: () => api<{ items: PersonRow[] }>(`/api/directory/people?q=${encodeURIComponent(q)}`) });
  const sync = useMutation({
    mutationFn: () => post<{ created: number; linked: number; unchanged: number; deactivated: number; total: number }>("/api/directory/people/sync-central", {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["people"] }),
  });
  const create = useMutation({
    mutationFn: () => post("/api/directory/people", { displayName: form.displayName, email: form.email || null }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["people"] }); setAdding(false); setForm({ displayName: "", email: "" }); },
  });
  return (
    <div>
      <PageHeader
        title="People"
        subtitle="Everyone exists once. People who sign in with their Infinite Fusion account appear here automatically."
        actions={can("directory.person.manage") && (
          <>
            <button className="btn" disabled={sync.isPending} onClick={() => sync.mutate()} title="Create a Person for every Infinite Fusion account">
              {sync.isPending ? "Importing…" : "Import central accounts"}
            </button>
            <button className="btn" onClick={() => setAdding(true)}><Plus size={16} /> Add person</button>
            {can("authz.grant.manage") && <button className="btn btn-primary" onClick={() => setInviting(true)}><Send size={16} /> Invite</button>}
          </>
        )}
      />
      <ErrorNote error={sync.error} />
      {sync.data && (
        <p className="mb-3 text-sm text-emerald-600">
          Imported {sync.data.total} accounts: {sync.data.created} new, {sync.data.linked} linked by email, {sync.data.unchanged} already present{sync.data.deactivated ? `, ${sync.data.deactivated} deactivated` : ""}.
        </p>
      )}
      <div className="relative mb-3 max-w-sm">
        <Search size={16} className="muted absolute left-3 top-2.5" />
        <input className="input !pl-9" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" />
      </div>
      <Card>
        {people.isLoading ? <Empty>Loading…</Empty> : people.data?.items.length ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead><tr><th>Name</th><th>Email</th><th>Position</th><th>Roles</th><th>Last seen</th><th /></tr></thead>
              <tbody>
                {people.data.items.map((p) => (
                  <tr key={p.id}>
                    <td><Link to="/admin/people/$id" params={{ id: p.id }} className="flex items-center gap-2.5 font-semibold hover:text-brand-700"><Avatar name={p.displayName} size={30} />{p.displayName}{p.status === "pending" && <span className="chip !text-[10px] text-amber-700">Not signed in yet</span>}{p.status === "inactive" && <span className="chip !text-[10px]">Inactive</span>}</Link></td>
                    <td className="muted">{p.email ?? "—"}</td>
                    <td>{p.positionTitle ?? "—"}</td>
                    <td>{p.grantCount > 0 ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">{p.grantCount} role{p.grantCount > 1 ? "s" : ""}</span> : <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">No access</span>}</td>
                    <td className="muted">{fmtDate(p.lastSeenAt)}</td>
                    <td className="text-right">{can("directory.person.manage") && p.email && p.status !== "inactive" && <button className="btn btn-ghost btn-sm" title="Send the invite email: how to sign in to ONE" aria-label={`Send ${p.displayName} the invite email`} onClick={() => setInviteFor(p)}><Send size={14} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No people found.</Empty>}
      </Card>
      {inviting && <InviteDialog onClose={() => setInviting(false)} />}
      {inviteFor && <SendInviteDialog personId={inviteFor.id} name={inviteFor.displayName} email={inviteFor.email!} onClose={() => setInviteFor(null)} />}
      {adding && (
        <Modal title="Add person" onClose={() => setAdding(false)} footer={<><button className="btn" onClick={() => setAdding(false)}>Cancel</button><button className="btn btn-primary" disabled={!form.displayName || create.isPending} onClick={() => create.mutate()}>Save</button></>}>
          <ErrorNote error={create.error} />
          <Field label="Full name"><input className="input" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></Field>
          <Field label="Email" hint="When they sign in with this email, their account links to this person."><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        </Modal>
      )}
    </div>
  );
}

interface PersonDetail {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  status: string;
  lastSeenAt: string | null;
  employments: Array<Record<string, string | null | boolean>>;
  teams: Array<{ id: string; name: string; kind: string; role: string }>;
}

function EmploymentDialog({ personId, onClose }: { personId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const org = useOrg();
  const [f, setF] = useState<Record<string, string>>({ workforce: "office", status: "active" });
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const people = useQuery({ queryKey: ["people", ""], queryFn: () => api<{ items: PersonRow[] }>("/api/directory/people") });
  const m = useMutation({
    mutationFn: () => post("/api/directory/employments", {
      personId, companyId: f.companyId, businessUnitId: f.businessUnitId || null, departmentId: f.departmentId || null, siteId: f.siteId || null,
      positionTitle: f.positionTitle || null, managerPersonId: f.managerPersonId || null, workforce: f.workforce, status: f.status,
      startDate: f.startDate || null,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["person", personId] }); onClose(); },
  });
  const o = org.data;
  return (
    <Modal title="Set employment" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!f.companyId || m.isPending} onClick={() => m.mutate()}>Save</button></>}>
      <ErrorNote error={m.error} />
      {o && (
        <>
          <Field label="Company"><select className="input" value={f.companyId ?? ""} onChange={set("companyId")}><option value="">Choose…</option>{o.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="Business unit"><select className="input" value={f.businessUnitId ?? ""} onChange={set("businessUnitId")}><option value="">—</option>{o.businessUnits.filter((b) => b.companyId === f.companyId).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></Field>
          <Field label="Department"><select className="input" value={f.departmentId ?? ""} onChange={set("departmentId")}><option value="">—</option>{o.departments.filter((d) => d.companyId === f.companyId).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
          <Field label="Site"><select className="input" value={f.siteId ?? ""} onChange={set("siteId")}><option value="">—</option>{o.sites.filter((s) => s.companyId === f.companyId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
        </>
      )}
      <Field label="Position"><input className="input" value={f.positionTitle ?? ""} onChange={set("positionTitle")} /></Field>
      <Field label="Manager"><select className="input" value={f.managerPersonId ?? ""} onChange={set("managerPersonId")}><option value="">—</option>{people.data?.items.filter((p) => p.id !== personId).map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}</select></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Workforce"><select className="input" value={f.workforce} onChange={set("workforce")}>{["office", "field", "frontline", "external"].map((x) => <option key={x}>{x}</option>)}</select></Field>
        <Field label="Start date"><input className="input" type="date" value={f.startDate ?? ""} onChange={set("startDate")} /></Field>
      </div>
    </Modal>
  );
}

export function PersonPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const can = useCan();
  const [granting, setGranting] = useState(false);
  const [employing, setEmploying] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [changingAccess, setChangingAccess] = useState(false);
  const person = useQuery({ queryKey: ["person", id], queryFn: () => api<PersonDetail>(`/api/directory/people/${id}`) });
  const grants = useQuery({ queryKey: ["grants", id], queryFn: () => api<{ items: Grant[] }>(`/api/authz/grants?personId=${id}`), enabled: can("authz.grant.view") });
  const org = useOrg();
  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    org.data?.companies.forEach((x) => m.set(x.id, x.name));
    org.data?.departments.forEach((x) => m.set(x.id, x.name));
    org.data?.businessUnits.forEach((x) => m.set(x.id, x.name));
    org.data?.sites.forEach((x) => m.set(x.id, x.name));
    return (v: unknown) => (typeof v === "string" ? (m.get(v) ?? "—") : "—");
  }, [org.data]);
  if (person.isLoading) return <Empty>Loading…</Empty>;
  if (!person.data) return <ErrorNote error={person.error} />;
  const p = person.data;
  return (
    <div className="space-y-4">
      <PageHeader title={p.displayName} subtitle={<>{p.email ?? "no email"} · last seen {fmtDate(p.lastSeenAt)}</>}
        actions={can("directory.person.manage") && p.email && p.status !== "inactive" && <button className="btn" onClick={() => setSendingInvite(true)}><Send size={15} /> Send invite email</button>} />
      {sendingInvite && <SendInviteDialog personId={p.id} name={p.displayName} email={p.email!} onClose={() => setSendingInvite(false)} />}
      <Card title="Employment" actions={can("directory.person.manage") && <button className="btn" onClick={() => setEmploying(true)}><Plus size={14} /> Set</button>}>
        {p.employments.length === 0 ? <Empty>No employment recorded.</Empty> : (
          <ul className="space-y-2 text-sm">
            {p.employments.map((e) => (
              <li key={String(e.id)} className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="font-medium">{(e.position_title as string) ?? "—"}</span>
                <span>{nameOf(e.company_id)}</span>
                <span className="muted">{nameOf(e.department_id)}</span>
                <span className="chip">{String(e.workforce)}</span>
                <span className="chip">{String(e.status)}</span>
                {e.is_primary && <span className="chip">primary</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {can("authz.grant.view") && grants.data && (() => {
        const cur = currentAccess(grants.data.items);
        return (
          <Card title="Access" actions={can("authz.grant.manage") && <button className="btn btn-primary" onClick={() => setChangingAccess(true)}>Change</button>}>
            <p className="text-sm font-semibold">{cur.label}</p>
            <p className="muted text-xs">{cur.label.startsWith("Group Management") ? TEMPLATE_HELP.management : cur.label === "Manager" ? TEMPLATE_HELP.manager : cur.label === "Staff" ? TEMPLATE_HELP.staff : "Give them Staff, Manager or Group Management."}</p>
            {cur.custom.length > 0 && <p className="muted mt-1 text-xs">Also has: {cur.custom.join(", ")} (see Roles in ONE below).</p>}
            {changingAccess && <AccessDialog personId={id} start={cur} onClose={() => setChangingAccess(false)} />}
          </Card>
        );
      })()}
      {can("authz.grant.view") && (
        <Card title="Roles in ONE (advanced)" actions={can("authz.grant.manage") && <button className="btn btn-primary" onClick={() => setGranting(true)}><Plus size={14} /> Grant role</button>}>
          {grants.isLoading ? <Empty>Loading…</Empty> : <GrantList grants={grants.data?.items ?? []} canManage={can("authz.grant.manage")} />}
        </Card>
      )}
      {granting && <GrantDialog personId={id} onClose={() => setGranting(false)} />}
      {employing && <EmploymentDialog personId={id} onClose={() => setEmploying(false)} />}
    </div>
  );
}

export function GrantsPage() {
  const can = useCan();
  const [role, setRole] = useState("");
  const roles = useRoles();
  const grants = useQuery({ queryKey: ["grants", "all", role], queryFn: () => api<{ items: Grant[] }>(`/api/authz/grants${role ? `?roleKey=${role}` : ""}`) });
  return (
    <div>
      <PageHeader title="Access" subtitle="Who holds which role, and where it applies." />
      <div className="mb-3 max-w-xs">
        <select className="input" value={role} onChange={(e) => setRole(e.target.value)} aria-label="Filter by role">
          <option value="">All roles</option>
          {roles.data?.items.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
      </div>
      <Card>
        {grants.isLoading ? <Empty>Loading…</Empty> : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead><tr><th>Person</th><th>Role</th><th>Scope</th><th>Reason</th><th>Since</th></tr></thead>
              <tbody>
                {grants.data?.items.map((g) => (
                  <tr key={g.id}>
                    <td><Link to="/admin/people/$id" params={{ id: g.personId }} className="font-medium text-brand-700 hover:underline dark:text-brand-300">{g.personName}</Link></td>
                    <td>{g.roleName}</td>
                    <td>{g.scopeType === "group" ? "Whole group" : `${g.scopeType.replace("_", " ")}: ${g.scopeName ?? "?"}`}</td>
                    <td className="muted">{g.reason ?? "—"}</td>
                    <td className="muted">{fmtDate(g.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!can("authz.grant.manage") && <p className="muted mt-2 text-xs">Read-only: you can't change access.</p>}
          </div>
        )}
      </Card>
    </div>
  );
}

function RoleEditor({ role, onClose }: { role: Role | null; onClose: () => void }) {
  const qc = useQueryClient();
  const perms = useQuery({ queryKey: ["permissions"], queryFn: () => api<{ items: Permission[] }>("/api/authz/permissions") });
  const [key, setKey] = useState(role?.key ?? "");
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.permissions ?? []));
  const m = useMutation({
    mutationFn: () => {
      const body = { key, name, description, permissions: [...selected] };
      return role ? patch(`/api/authz/roles/${role.id}`, body) : post("/api/authz/roles", body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["roles"] }); onClose(); },
  });
  const groups = useMemo(() => {
    const g = new Map<string, Permission[]>();
    perms.data?.items.forEach((p) => { const k = p.key.split(".")[0]!; g.set(k, [...(g.get(k) ?? []), p]); });
    return [...g.entries()];
  }, [perms.data]);
  const toggle = (k: string) => setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const wildcard = selected.has("*");
  return (
    <Modal title={role ? `Edit role: ${role.name}` : "New role"} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!key || !name || m.isPending} onClick={() => m.mutate()}>Save</button></>}>
      <ErrorNote error={m.error} />
      {!role && <Field label="Key" hint="lowercase_with_underscores"><input className="input" value={key} onChange={(e) => setKey(e.target.value)} /></Field>}
      <Field label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Description"><input className="input" value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      {wildcard ? <p className="text-sm">This role has every permission (*).</p> : (
        <div className="max-h-80 space-y-3 overflow-auto">
          {groups.map(([mod, list]) => (
            <fieldset key={mod}>
              <legend className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                {mod}
                <label className="muted flex items-center gap-1 font-normal normal-case"><input type="checkbox" checked={selected.has(`${mod}.*`)} onChange={() => toggle(`${mod}.*`)} /> all</label>
              </legend>
              {list.map((p) => (
                <label key={p.key} className="flex items-start gap-2 py-0.5 text-sm">
                  <input type="checkbox" className="mt-1" disabled={selected.has(`${mod}.*`)} checked={selected.has(p.key) || selected.has(`${mod}.*`)} onChange={() => toggle(p.key)} />
                  <span>{p.description} <span className="muted text-xs">{p.key}</span>{(p.risk === "high" || p.risk === "critical") && <span className="chip ml-1 text-amber-600">{p.risk}</span>}</span>
                </label>
              ))}
            </fieldset>
          ))}
        </div>
      )}
    </Modal>
  );
}

export function RolesPage() {
  const roles = useRoles();
  const can = useCan();
  const [editing, setEditing] = useState<Role | null | "new">(null);
  return (
    <div>
      <PageHeader title="Roles" subtitle="What each role is allowed to do." actions={can("authz.role.manage") && <button className="btn btn-primary" onClick={() => setEditing("new")}><Plus size={16} /> New role</button>} />
      <div className="grid gap-3 md:grid-cols-2">
        {roles.data?.items.map((r) => (
          <Card key={r.id} title={<>{r.name} {r.isSystem && <span className="chip ml-1">default</span>}</>} actions={can("authz.role.manage") && <button className="btn !min-h-0 !py-1 text-xs" onClick={() => setEditing(r)}>Edit</button>}>
            <p className="muted text-sm">{r.description}</p>
            <p className="mt-2 text-xs"><span className="font-medium">{r.permissions.includes("*") ? "All permissions" : `${r.permissions.length} permissions`}</span> · {r.activeGrants} people · v{r.version}</p>
          </Card>
        ))}
      </div>
      {editing && <RoleEditor role={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
